import {
  orderCenterRoutes,
  profileRoutes,
  subscriptionsRoutes,
} from '@src/routes'

export interface MegaNavState {
  activeNavItem: Maybe<string>
  activeSubNavItem: Maybe<string>
}

export enum MegaNavActionTypes {
  SET_ACTIVE_NAV_ITEM = 'setActiveNavItem',
  SET_ACTIVE_SUBNAV_ITEM = 'setActiveSubNavItem',
  CLOSE = 'close,',
}

export type MegaNavAction =
  | { type: MegaNavActionTypes.SET_ACTIVE_NAV_ITEM; payload: string | null }
  | { type: MegaNavActionTypes.SET_ACTIVE_SUBNAV_ITEM; payload: string | null }
  | { type: MegaNavActionTypes.CLOSE }

type NavItemUIType = 'link' | 'divider'

export interface NavItem {
  label: string
  eventLabel?: string
  url?: string
  items?: NavItem[]
  suffix?: React.ReactNode
  type?: NavItemUIType
  level?: string
}

export enum MegaNavSections {
  Products = 'PRODUCTS',
  Industries = 'INDUSTRIES',
  Services = 'SERVICES',
  Account = 'PRIMARY_NAV_ACCOUNT',
}

type MegaNavData = { [K in MegaNavSections]: NavItem }

export const MEGA_NAV_ITEMS: (
  isOrderStatusCustomer: boolean,
  isQuoteEnabled: boolean
) => MegaNavData = (isOrderStatusCustomer, isQuoteEnabled) => ({
  [MegaNavSections.Products]: {
    label: 'PRODUCTS',
    items: [
      {
        label: 'ANALYTICAL_CHROMATOGRAPHY',
        items: [
          {
            label: 'ANALYTICAL_REAGENTS',
            url: '/analytical-chromatography/analytical-reagents',
          },
          {
            label: 'ANALYTICAL_STANDARDS',
            url: '/analytical-chromatography/analytical-standards',
          },
          {
            label: 'FOOD_ANALYSIS',
            url: '/analytical-chromatography/food-analysis',
          },
          {
            label: 'GAS_CHROMATOGRAPHY',
            url: '/analytical-chromatography/gas-chromatography',
          },
          {
            label: 'HPLC',
            url: '/analytical-chromatography/hplc',
          },
          {
            label: 'SAMPLE_PREPARATION',
            url: '/analytical-chromatography/sample-preparation',
          },
          {
            label: 'TITRATION',
            url: '/analytical-chromatography/titration',
          },
          {
            label: 'SEE_ALL_ANALYTICAL_CHROMATOGRAPHY',
            url: '/analytical-chromatography',
          },
        ],
      },
      {
        label: 'CHEMISTRY',
        items: [
          {
            label: 'CHEMICAL_SYNTHESIS',
            url: '/chemistry/chemical-synthesis',
          },
          {
            label: 'SOLVENTS',
            url: '/chemistry/solvents',
          },
          {
            label: 'STABLE_ISOTOPES',
            url: '/chemistry/stable-isotopes-isotec',
          },
          {
            label: 'STOCKROOM_REAGENTS',
            url: '/chemistry/stockroom-reagents',
          },
          {
            label: 'SEE_ALL_CHEMISTRY',
            url: '/chemistry',
          },
        ],
      },
      {
        label: 'LIFE_SCIENCE',
        items: [
          {
            label: 'THREE_D_CELL_CULTURE',
            url: '/life-science/3d-cell-culture',
          },
          {
            label: 'ANTIBIOTICS',
            url: '/life-science/antibiotics',
          },
          {
            label: 'ANTIBODIES',
            url: '/life-science/antibodies',
          },
          {
            label: 'BIOCHEMICALS',
            url: '/life-science/biochemicals',
          },
          {
            label: 'CELL_ANALYSIS',
            url: '/life-science/cell-analysis',
          },
          {
            label: 'CELL_BIOLOGY',
            url: '/life-science/cell-biology',
          },
          {
            label: 'CELL_CULTURE',
            url: '/life-science/cell-culture',
          },
          {
            label: 'CORE_BIOREAGENTS',
            url: '/life-science/core-bioreagents',
          },
          {
            label: 'CRISPR_CAS_9',
            url: '/life-science/crispr-cas9',
          },
          {
            label: 'DNA_DAMAGE_AND_REPAIR',
            url: '/life-science/dna-damage-and-repair',
          },
          {
            label: 'DUOLINK_AND_PLA_TECHNOLOGY',
            url: '/life-science/duolink-and-pla-technology',
          },
          {
            label: 'ELISA_KIT',
            url: '/life-science/elisa-kit',
          },
          {
            label: 'FUNCTIONAL_GENOMICS_RNAI',
            url: '/life-science/functional-genomics-and-rnai',
          },
          {
            label: 'METABOLOMICS',
            url: '/life-science/metabolomics',
          },
          {
            label: 'MOLECULAR_BIOLOGY',
            url: '/life-science/molecular-biology',
          },
          {
            label: 'PROTEASE_INHIBITORS',
            url: '/life-science/protease-inhibitors',
          },
          {
            label: 'PROTEIN_EXTRACTION',
            url: '/life-science/protein-extraction',
          },
          {
            label: 'PROTEOMICS',
            url: '/life-science/proteomics',
          },
          {
            label: 'ROCHE_BIOCHEMICAL_REAGENTS',
            url: '/life-science/roche-biochemical-reagents',
          },
          {
            label: 'ROCHE_PRODUCTS',
            url: '/life-science/roche-products',
          },
          {
            label: 'STEM_CELL_BIOLOGY',
            url: '/life-science/stem-cell-biology',
          },
          {
            label: 'TRANSFECTION',
            url: '/life-science/transfection',
          },
          {
            label: 'TRANSLATIONAL_RESEARCH_SOLUTIONS',
            url: '/life-science/translational-research-solutions',
          },
          {
            label: 'WESTERN_BLOTTING',
            url: '/life-science/western-blotting',
          },
          {
            label: 'SEE_ALL_LIFE_SCIENCE',
            url: '/life-science',
          },
        ],
      },
      {
        label: 'MATERIALS_SCIENCE',
        items: [
          {
            label: 'NANOMATERIALS',
            url: '/materials-science/nanomaterials',
          },
          {
            label: 'POLYMER_SCIENCE',
            url: '/materials-science/polymer-science',
          },
          {
            label: 'SEE_ALL_MATERIALS_SCIENCE',
            url: '/materials-science',
          },
        ],
      },
      { label: 'MICROBIOLOGY', url: '/industrial-microbiology' },
      {
        label: 'VETEC',
        items: [
          {
            label: 'WESTERN_BLOT',
            url: '/vetec/western-blot',
          },
          {
            label: 'SEE_ALL_VETEC',
            url: '/vetec',
          },
        ],
      },
      { label: 'LABWARE', url: '/labware' },
      {
        label: 'CUSTOM_OLIGOS',
        url: '/configurators/customOligo',
      },
      { label: 'SEE_ALL_PRODUCTS', url: '/product-catalog' },
    ],
  },
  [MegaNavSections.Services]: {
    label: 'SERVICES',
    url: '/services',
  },
  [MegaNavSections.Industries]: {
    label: 'INDUSTRIES',
    items: [
      {
        label: 'AGRICULTURE',
        url: '/industries/agriculture',
      },
      {
        label: 'CHEMICAL_MANUFACTURING',
        url: '/industries/chemical-manufacturing',
      },
      {
        label: 'CLINICAL_TESTING',
        url: '/industries/clinical-testing',
      },
      {
        label: 'FOOD_AND_BEVERAGE',
        url: '/industries/food-and-beverage',
      },
      {
        label: 'PHARMACEUTICAL',
        url: '/industries/pharma',
      },
      { label: 'SEE_ALL_INDUSTRIES', url: '/industries' },
    ],
  },
  [MegaNavSections.Account]: {
    label: 'PRIMARY_NAV_ACCOUNT',
    items: [
      {
        label: 'YOUR_PROFILE',
        url: profileRoutes.index(),
      },
      {
        label: 'top-divider',
        type: 'divider',
      },
      {
        label: 'PRIMARY_NAV_ACCOUNT',
        url: orderCenterRoutes.dashboard(),
      },
      {
        label: 'YOUR_ORDERS',
        url: isOrderStatusCustomer
          ? orderCenterRoutes.orderSearch()
          : orderCenterRoutes.orders(),
      },
      ...(isQuoteEnabled
        ? [
            {
              label: 'YOUR_QUOTES',
              url: orderCenterRoutes.quotes(),
            },
          ]
        : []),
      {
        label: 'SAVED_CARTS',
        url: orderCenterRoutes.savedCarts(),
      },
      {
        label: 'BUY_IT_AGAIN',
        url: orderCenterRoutes.buyItAgain(),
      },
      {
        label: 'LISTS',
        url: orderCenterRoutes.lists(),
      },
      {
        label: 'bottom-divider',
        type: 'divider',
      },
      {
        label: 'SUBSCRIPTIONS',
        url: subscriptionsRoutes.index(),
      },
    ],
  },
})
